import { useEffect, useRef, useState, type ReactNode } from 'react';
import { create } from 'zustand';
import { api, ApiClientError, onStepUpRequired } from '../api/client.js';
import { Icon } from '../../components/ui/Icon.js';
import { Modal } from '../../components/ui/Modal.js';
import { useSession } from './session.js';

/**
 * Step-up, as a question rather than an error.
 *
 * `auth.step_up_required` is not a failure: the session is valid and the
 * permission is held. The API is asking the person to prove they are still the
 * person, before something that moves money or reveals a credential. Reporting
 * it as an error message is a dead end — the user is told to re-verify with
 * nothing to re-verify with.
 *
 * That dead end was live: SipCredential asked properly and retried, while the
 * balance-adjustment form printed "Re-verify your identity to continue" and
 * offered no way to do it. One of the two was right, so it is generalised here
 * rather than copied (ADR-019) and both now use it.
 *
 * It became reachable at all because logging in used to stamp `sessions.mfa_at`,
 * which satisfied the five-minute window without anybody ever being asked. The
 * server no longer does that, so this is now the only path through.
 *
 *   const step = useStepUp();
 *   step.run(() => adjust.mutateAsync(body));    // prompts and retries by itself
 *   ...
 *   {step.challenge && <StepUpPrompt step={step} what="Adjusting a balance" />}
 */

const STEP_UP = 'auth.step_up_required';

export interface StepUp {
  /** True while the password is being asked for. */
  challenge: boolean;
  busy: boolean;
  error: string | null;
  password: string;
  setPassword: (v: string) => void;
  /** Runs the action; on a step-up refusal, holds it and raises the challenge. */
  run: <T>(action: () => Promise<T>) => Promise<T | undefined>;
  /** Verifies the password (or the 2FA code) and replays the held action. */
  verify: () => Promise<void>;
  /** Two-step verification is on: the prompt asks for the app's code, not the password. */
  twoStep: boolean;
  cancel: () => void;
}

export function useStepUp(): StepUp {
  const twoStep = useSession((s) => s.session?.user.mfa_enabled ?? false);
  const [challenge, setChallenge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  /**
   * The action is held in state, not re-derived, so the replay after
   * verification is the SAME call with the same arguments. Asking someone to
   * prove who they are and then making them fill the form in again is how a
   * security control becomes the thing people route around.
   */
  const [pending, setPending] = useState<null | (() => Promise<unknown>)>(null);

  const run = async <T,>(action: () => Promise<T>): Promise<T | undefined> => {
    setError(null);
    try {
      return await action();
    } catch (e) {
      if (e instanceof ApiClientError && e.code === STEP_UP) {
        setPending(() => action);
        setChallenge(true);
        return undefined;
      }
      // Declined in the portal-wide prompt (StepUpHost): nothing happened, and that is not an error.
      if (e instanceof ApiClientError && e.code === 'auth.step_up_cancelled') return undefined;
      throw e;
    }
  };

  const verify = async () => {
    setBusy(true); setError(null);
    try {
      await api.post('/v1/auth/step-up', twoStep ? { code: password } : { password });
      const held = pending;
      setPassword(''); setChallenge(false); setPending(null);
      if (held) await held();
    } catch (e) {
      setError(e instanceof ApiClientError && e.status === 401
        ? (twoStep ? 'That code is not correct' : 'That password is not correct')
        : 'Could not verify your identity');
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setChallenge(false); setPending(null); setPassword(''); setError(null);
  };

  return { challenge, busy, error, password, setPassword, run, verify, cancel, twoStep };
}

export function StepUpPrompt({ step, what, children }: {
  step: StepUp;
  /** What is being confirmed, e.g. "Adjusting a balance". Becomes the sentence. */
  what: string;
  children?: ReactNode;
}) {
  /**
   * Brought into view when it appears (26 September 2026): it sits at the top of the page, and a
   * "Show password" clicked further down asked for a password nobody could see -- the button looked
   * dead. Scrolled to the middle of the screen, then the box takes the cursor.
   */
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!step.challenge) return;
    box.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    input.current?.focus({ preventScroll: true });
  }, [step.challenge]);
  if (!step.challenge) return null;
  return (
    <div ref={box} className="arc-banner arc-banner-info" style={{ marginBottom: 12 }}>
      <Icon name="warning" size={14} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Confirm it is you</div>
        <p className="arc-muted" style={{ fontSize: 12.5, margin: '0 0 8px' }}>
          {what} is a sensitive action.{' '}
          {step.twoStep ? 'Enter the code from your authenticator app to continue.' : 'Enter your own portal password to continue.'}
          {' '}{children}
        </p>
        <div className="arc-row" style={{ gap: 8 }}>
          <input ref={input} className={step.twoStep ? 'arc-input arc-mono' : 'arc-input'}
            type={step.twoStep ? 'text' : 'password'}
            autoComplete={step.twoStep ? 'one-time-code' : 'current-password'}
            style={{ maxWidth: 260 }} value={step.password}
            placeholder={step.twoStep ? '6-digit code' : 'Your portal password'}
            onChange={(e) => step.setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && step.password) void step.verify();
            }} />
          <button type="button" className="arc-btn arc-btn-primary"
            disabled={step.busy || !step.password} onClick={() => void step.verify()}>
            {step.busy ? 'Checking…' : 'Confirm'}
          </button>
          <button type="button" className="arc-btn arc-btn-ghost" onClick={step.cancel}>
            Cancel
          </button>
        </div>
        {step.error && (
          <div style={{ color: 'var(--arc-danger)', fontSize: 12.5, marginTop: 8 }}>
            {step.error}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ the one prompt

interface HostState {
  /** Everybody waiting on this one confirmation: several requests can need it at once. */
  waiting: Array<(ok: boolean) => void>;
  ask: () => Promise<boolean>;
  settle: (ok: boolean) => void;
}

const useHost = create<HostState>((set, get) => ({
  waiting: [],
  ask: () => new Promise<boolean>((resolve) => set({ waiting: [...get().waiting, resolve] })),
  settle: (ok) => {
    const w = get().waiting;
    set({ waiting: [] });
    w.forEach((r) => r(ok));
  },
}));

/**
 * "Confirm it is you", for any action the API asks it for (lib/api/client.ts). Mounted once beside
 * the confirm dialog (app/App.tsx). The password, or the authenticator code when 2FA is on; after
 * it, the request that asked goes through by itself.
 */
export function StepUpHost() {
  const open = useHost((s) => s.waiting.length > 0);
  const settle = useHost((s) => s.settle);
  const twoStep = useSession((s) => s.session?.user.mfa_enabled ?? false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onStepUpRequired(() => useHost.getState().ask());
    return () => onStepUpRequired(null);
  }, []);
  useEffect(() => {
    if (!open) { setValue(''); setError(null); return; }
    const id = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  if (!open) return null;
  const verify = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      await api.post('/v1/auth/step-up', twoStep ? { code: value.trim() } : { password: value });
      settle(true);
    } catch (e) {
      setError(e instanceof ApiClientError && e.status === 401
        ? (twoStep ? 'That code is not correct.' : 'That password is not correct.')
        : e instanceof ApiClientError && e.status === 429 ? e.message
          : 'Could not check that. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Confirm it is you" size="sm" onClose={() => settle(false)}>
      <form className="arc-confirm arc-stack" style={{ gap: 12 }} onSubmit={(e) => { e.preventDefault(); if (value) void verify(); }}>
        <p className="arc-muted" style={{ margin: 0, fontSize: 13 }}>
          {twoStep ? 'Enter the code from your authenticator app to carry on.' : 'Enter your portal password to carry on.'}
          {' '}You will not be asked again for five minutes.
        </p>
        <input ref={input} className={twoStep ? 'arc-input arc-mono' : 'arc-input'}
          type={twoStep ? 'text' : 'password'} inputMode={twoStep ? 'numeric' : undefined}
          autoComplete={twoStep ? 'one-time-code' : 'current-password'}
          aria-label={twoStep ? 'Code from your app' : 'Your password'}
          placeholder={twoStep ? '6-digit code' : 'Your portal password'}
          value={value} onChange={(e) => setValue(e.target.value)} />
        {error && <div className="arc-error" role="alert" style={{ margin: 0 }}>{error}</div>}
        <div className="arc-confirm-actions">
          <button type="button" className="arc-btn" onClick={() => settle(false)}>Cancel</button>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={busy || !value}>
            {busy ? 'Checking…' : 'Confirm'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
